import { Badge, Button } from 'reactstrap'
import { ChevronDown, ChevronUp, X } from 'react-feather'
import {
  selectChat,
  setIsNewChatOpen,
  setOnSelect,
  setSelectedUser,
  setUserDeleted,
  upsertRecentChat,
} from '../../redux/reducers/messenger/chatSlice'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import { useEffect, useRef, useState } from 'react'

import ChatAvatar from '../../shared/image/ChatAvatar'
import ChatBox from './ChatBox'
import Drawer from './common/Drawer'
import { ChatType, IWebComponentProps } from '../../types/webComponent'
import LeftSidebarChat from '../messenger/left-sidebar-chat'
import React from 'react'
import { RecentChat } from '../../types/components/chat'
import StickyHeader from './StickyHeader'
import { getChatWindowPosition } from '../../Webcomponent/utils/common'
import { queries } from '../../api'
import { setMobileMenu } from '../../redux/reducers/messenger/messengerSlice'
import { useChatWindows } from '../../hooks/useChatWindowLimit'
import useWheelScrollTrap from '../../hooks/useWheelScrollTrap'
import { useQueryClient } from '@tanstack/react-query'
import { KEYS } from '../../constants/keys'

const ChatList = (props: IWebComponentProps) => {
  const [open, setOpen] = useState(false)
  // Desktop default: the panel's 10px right offset + its 310px width.
  const [panelSpan, setPanelSpan] = useState(320)
  const { onRegisterStartChat, type } = props
  const { data } = queries.useGetUserDetails()
  const { newChat, activeChat, allOpenChats, startChat, closeChat, setActiveChat } = useChatWindows()
  const [isMinimize, setIsMinimize] = useState<any>({})
  const { selectedUser, onSelect, deleteUserId } = useAppSelector((state) => state.chat)
  const dispatch = useAppDispatch()
  const [username, setUsername] = useState<string | null>('')
  const { data: results } = queries.useGetContactDetails(username as string)
  const queryClient = useQueryClient()
  const allMessagesRef = useRef<any>(null)
  const stickyBoxRef = useRef<HTMLDivElement>(null)
  const footerChatRef = useRef<HTMLDivElement>(null)
  const [recentChatsList, setRecentChatsList] = useState<RecentChat[]>([])
  const { data: recentChatsData } = queries.useGetRecentChats()

  // Keep wheel scrolling inside the floating widget from scrolling the host page.
  useWheelScrollTrap(footerChatRef)

  useEffect(() => {
    if (!recentChatsData) return
    if (recentChatsData.chats && recentChatsData.chats.length > 0) {
      const list = [...recentChatsList, ...recentChatsData.chats]
      // Deduplicate by conversation identity (chat_type + chat_id), NOT email.
      // Emails can be null/duplicate, which collapsed distinct users into one and
      // left freshly-opened chats unfindable — storeActiveChat then failed to
      // resolve the active user and kept the previous selectedUser, so a second
      // window appeared to show the same user.
      const uniqueList = Array.from(
        new Map(list.map((item) => [`${item.chat_type}_${String(item.chat_id)}`, item])).values(),
      )
      setRecentChatsList(uniqueList)
    } else {
      setRecentChatsList([])
    }
  }, [dispatch, recentChatsData])

  useEffect(() => {
    if (!results || !username) return
    dispatch(
      selectChat({
        id: results.chat_id,
        type: results.chat_type,
        pinned: results.isPinned,
      }),
    )
    setUsername(null)
    dispatch(setIsNewChatOpen(false))
    dispatch(setSelectedUser(results))
    dispatch(setOnSelect(true))
    dispatch(setMobileMenu())
    // Insert the new contact directly into the Redux recent-chats list so the
    // sticky sidebar shows it immediately. The server won't return this contact
    // in recent-chats until a message is sent.
    // Guard is explicit even though ChatList.tsx only ever mounts in CHAT_LIST
    // mode, so this block can never run on the Messages page.
    if (type === ChatType.CHAT_LIST) {
      dispatch(
        upsertRecentChat({
          chat_id: results.chat_id,
          chat_type: (results.chat_type ?? 'direct') as 'direct' | 'group',
          name: results.name ?? '',
          avatar: results.avatar ?? '',
          email: results.email ?? null,
          status: results.status,
          unreadCount: 0,
          isMuted: results.isMuted ?? false,
          isPinned: results.isPinned ?? false,
          isArchived: false,
          pinned_at: null,
          lastMessage: null,
          onlineStatus: results.onlineStatus,
          is_verified: results.is_verified,
        } as any),
      )
      // Invalidate cache so the sparse entry is replaced with a full server
      // record (with lastMessage, timestamps etc.) on the next refetch.
      queryClient.invalidateQueries({ queryKey: [KEYS.RECENT_CHATS] })
    }
    if (
      results.chat_type &&
      !recentChatsList.some(
        (item) => String(item.chat_id) === String(results.chat_id) && item.chat_type === results.chat_type,
      )
    ) {
      setRecentChatsList([...recentChatsList, results as unknown as RecentChat])
    }
  }, [results])

  const toggleDrawer = (newOpen: boolean) => () => {
    // document.body.style.overflow = ''
    setOpen(newOpen)
    // if (newOpen) {
    //   setTimeout(() => {
    //     document.body.style.overflow = 'auto'
    //   }, 200)
    // }
  }

  const handleStartChat = (chatId: number | string) => {
    startChat(chatId)
  }

  // Register once
  useEffect(() => {
    if (allOpenChats.length === 0) {
      dispatch(setOnSelect(false))
    }
  }, [allOpenChats])

  const handleSelectedUserId = (uname: any) => {
    if (uname) {
      const name = (uname as string).split('_##_')[1]
      if (name) {
        setUsername(name as any)
        dispatch(setIsNewChatOpen(true))
      }
    }
  }

  const handleSelectedUserIdRef = useRef(handleSelectedUserId)
  handleSelectedUserIdRef.current = handleSelectedUserId

  // Register once
  useEffect(() => {
    if (onRegisterStartChat) {
      onRegisterStartChat((uname: any) => handleSelectedUserIdRef.current(uname))
    }
  }, [onRegisterStartChat])

  const storeActiveChat = (chatId: number | string) => {
    const recentChat = recentChatsList.find((chat: RecentChat) => String(chat.chat_id) === String(chatId)) as RecentChat
    if (recentChat && Object.keys(recentChat).length > 0) {
      dispatch(
        selectChat({
          id: recentChat.chat_id,
          type: recentChat.chat_type,
          pinned: recentChat.isPinned,
        }),
      )
      dispatch(setSelectedUser(recentChat))
    } else {
      dispatch(
        selectChat({
          id: selectedUser?.chat_id,
          type: selectedUser?.chat_type,
          pinned: selectedUser?.isPinned,
        }),
      )
      dispatch(setSelectedUser(selectedUser))
    }
  }

  // A window is expanded only when it is BOTH the active chat and not explicitly
  // minimized. Deriving "expanded" from the single activeChat guarantees at most
  // one window ever renders <ChatBox/> (which shows the global selectedUser), so
  // two windows can't both render the same user.
  const isWindowExpanded = (chatId: number | string) =>
    String(chatId) === String(activeChat) && isMinimize[chatId] !== true

  const minimizeChat = (id: number | string) => {
    if (isWindowExpanded(id)) {
      // Collapse the currently expanded window.
      setIsMinimize((prev: any) => ({ ...prev, [id]: true }))
    } else {
      // Expand this window: make it the active one and collapse the rest.
      setActiveChat(String(id))
      setIsMinimize(() => {
        const newMinimize: Record<string, boolean> = {}
        allOpenChats.forEach((chatId) => {
          newMinimize[chatId] = String(chatId) !== String(id)
        })
        return newMinimize
      })
      storeActiveChat(id)
    }
  }

  useEffect(() => {
    if (!newChat) return

    if (allOpenChats.length > 1) {
      // Rebuild isMinimize from allOpenChats (the source of truth),
      // not from Object.keys(prev) which may be stale/empty
      setIsMinimize(() => {
        const newMinimize: Record<string, boolean> = {}
        allOpenChats.forEach((id) => {
          newMinimize[id] = id !== (activeChat as any)
        })
        return newMinimize
      })
    } else if (activeChat && allOpenChats.length === 1) {
      const newMinimize: Record<string, boolean> = {}
      newMinimize[activeChat] = false
      setIsMinimize(newMinimize)
    }
  }, [newChat])

  // Separate useEffect to handle storeActiveChat after minimize state is set
  useEffect(() => {
    if (activeChat) {
      // Use a microtask to ensure minimize state has been applied
      Promise.resolve().then(() => {
        storeActiveChat(activeChat!)
      })
    }
  }, [activeChat, isMinimize])

  const closeWindow = (chatId: any) => {
    closeChat(chatId)
    setIsMinimize((prev: any) => {
      const updated = { ...prev }
      delete updated[chatId]
      return updated
    })
  }

  // Chat windows sit to the left of whichever panel is showing. The drawer stays
  // mounted when collapsed (it only slides off-screen), so measuring it alone
  // always reported the expanded width and left a wide gap beside the narrower
  // collapsed bar. Measure the visible element, and again on every toggle.
  // The span runs from the viewport's right edge to the panel's left edge, so
  // the panel's CSS `right` offset (10px, or 22px on narrow screens) is
  // included instead of assumed. clientWidth, not innerWidth: a fixed
  // element's `right` is measured from the edge inside the scrollbar.
  useEffect(() => {
    const updateLimit = () => {
      const visible = open ? allMessagesRef.current : stickyBoxRef.current
      if (visible) {
        setPanelSpan(Math.round(document.documentElement.clientWidth - visible.getBoundingClientRect().left))
      }
    }

    updateLimit()
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', updateLimit)
    }
    if (typeof window !== 'undefined') {
      return () => {
        window.removeEventListener('resize', updateLimit)
      }
    }
  }, [open])

  useEffect(() => {
    if (onSelect && selectedUser?.chat_id) {
      handleStartChat(selectedUser.chat_id as number)
    }
  }, [onSelect, selectedUser?.chat_id])

  useEffect(() => {
    if (deleteUserId) {
      closeChat(deleteUserId as number)
      closeWindow(deleteUserId)
      dispatch(setUserDeleted(null))
    }
  }, [deleteUserId])

  const messagingElements = (type: string) => {
    return (
      <div className="message-box" onClick={toggleDrawer(type === 'toggleDown' ? true : false)}>
        <div className="user-heading">
          <div className="chat-box">
            <ChatAvatar data={data?.user} name={data?.user} customClass="user-info avatar-sm" />
            <div className="details">
              <h6>Messaging</h6>
              <Badge color={'error'} className={'sm chat-badge'} />
            </div>
          </div>
        </div>
        <div className="sticky-header">
          <div className="action-btn">
            <Button className="toggle-btn" onClick={toggleDrawer(type === 'toggleDown' ? true : false)}>
              {<ChevronUp className="icon" />}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  const getIsMinimize = (chatId: number | string) => {
    return isWindowExpanded(chatId) ? 'minimize-in' : 'minimize-out'
  }

  // Normalized minimize map (true = minimized) derived from the active window,
  // so window positioning/width stays consistent with what's actually rendered
  // even if the raw isMinimize map has missing/stale entries.
  const minimizeMap: Record<string, boolean> = Object.fromEntries(
    allOpenChats.map((chatId) => [String(chatId), !isWindowExpanded(chatId)]),
  )

  const getMinimizedOutUserData = (chatId: number | string) => {
    const recentChat = recentChatsList.find((chat) => String(chat.chat_id) === String(chatId))
    return <StickyHeader userInfo={recentChat} handleHeaderClick={minimizeChat} />
  }

  return (
    <>
      <div className="footer_chat" ref={footerChatRef}>
        {!open && (
          <div className="sticky_wrapper">
            <div className="sticky_box" ref={stickyBoxRef}>
              {messagingElements('toggleDown')}
            </div>
          </div>
        )}

        <Drawer open={open} onClose={close} side="bottom">
          <div className="meetzy-container sidebar-toggle all-messages" ref={allMessagesRef}>
            {open && (
              <div className="sticky-header">
                <div className="action-btn">
                  <Button className="toggle-btn" onClick={toggleDrawer(false)}>
                    {<ChevronDown className="icon" />}
                  </Button>
                </div>
              </div>
            )}
            <LeftSidebarChat />
          </div>
        </Drawer>
        {onSelect && (
          <React.Fragment>
            {allOpenChats.length > 0 &&
              allOpenChats.map(
                (chatId: any, index) =>
                  chatId && (
                    <React.Fragment key={chatId}>
                      {isWindowExpanded(chatId) && <div className="overlay"></div>}
                      <div
                        key={chatId} // ✅ Always add a unique key when mapping
                        className={`chatWindow ${getIsMinimize(chatId)}`}
                        style={getChatWindowPosition(index, allOpenChats, minimizeMap, panelSpan)}
                      >
                        <div className="chatWindow-wrapper">
                          <div className="chatWindow-header">
                            <div className="d-flex align-items-center action-btn">
                              <Button className="minimize_chat" onClick={() => minimizeChat(chatId)}>
                                {isWindowExpanded(chatId) ? (
                                  <ChevronDown className="icon" />
                                ) : (
                                  <ChevronUp className="icon" />
                                )}
                              </Button>

                              <Button className="window-close" onClick={() => closeWindow(chatId)}>
                                <X />
                              </Button>
                            </div>
                          </div>

                          {isWindowExpanded(chatId) ? <ChatBox /> : <>{getMinimizedOutUserData(chatId)}</>}
                        </div>
                      </div>
                    </React.Fragment>
                  ),
              )}
          </React.Fragment>
        )}
      </div>
    </>
  )
}

export default ChatList
